import { Flex, Image, Text, Title } from "@qws-org/ui-kit";
import type { FC } from "react";

import { useDirectus } from "~/shared/utils/directus/useDirectus";

type ColumnElementProps = {
  title?: string | null;
  description?: string | null;
  picture?: {
    lazy_loading?: boolean | null;
    alternative_text?: string | null;
    image?: { id: string } | null;
    mobile_image_width?: number | null;
    mobile_image_height?: number | null;
    tablet_image?: { id: string } | null;
    tablet_image_width?: number | null;
    tablet_image_height?: number | null;
    desktop_image?: { id: string } | null;
    desktop_image_width?: number | null;
    desktop_image_height?: number | null;
  } | null;
};

export const ColumnElement: FC<ColumnElementProps> = ({
  title,
  description,
  picture,
}) => {
  const { getDirectusImage } = useDirectus();
  const mImageWidth = picture?.mobile_image_width ?? undefined;
  const mdImageWidth = picture?.tablet_image_width ?? mImageWidth;
  const dImageWidth = picture?.desktop_image_width ?? mdImageWidth;
  const mImageHeight = picture?.mobile_image_height ?? undefined;
  const mdImageHeight = picture?.tablet_image_height ?? mImageHeight;
  const dImageHeight = picture?.desktop_image_height ?? mdImageHeight;
  const mImage = picture?.image ?? undefined;
  const mdImage = picture?.tablet_image ?? mImage;
  const dImage = picture?.desktop_image ?? mdImage;

  return (
    <Flex
      bg="surface-primary-grad-subtle"
      border={{ radius: "16px" }}
      align="center"
      flexDirection="column"
      justify="center"
      gap={24}
      p={24}
      d={{
        gap: 24,
        minWidth: 400,
        maxWidth: 610,
        p: { py: 16 },
        flexDirection: "row",
        width: "fit-content",
      }}
      minWidth={312}
      maxWidth={400}
      width="100%"
      className="flex-[1_1_auto]"
    >
      {mImage?.id && (
        <Image
          value={getDirectusImage(mImage.id, {
            width: mImageWidth,
            height: mImageHeight,
            format: "webp",
          })}
          mdValue={
            mdImage
              ? getDirectusImage(mdImage.id, {
                  width: mdImageWidth,
                  height: mdImageHeight,
                  format: "webp",
                })
              : ""
          }
          dValue={
            dImage
              ? getDirectusImage(dImage.id, {
                  width: dImageWidth,
                  height: dImageHeight,
                  format: "webp",
                })
              : ""
          }
          overflow="hidden"
          image={{
            className: "object-cover object-center",
            alt: picture?.alternative_text ?? "",
          }}
          width={mImageWidth}
          height={mImageHeight}
          minWidth="fit-content"
          md={{ height: mdImageHeight, width: mdImageWidth }}
          d={{
            height: dImageHeight,
            width: dImageWidth,
          }}
        />
      )}
      <Flex className="flex-grow" justify="center" flexDirection="column">
        {title && (
          <Title
            as="h4"
            size="medium"
            fontWeight="mobile.bold"
            md={{
              fontWeight: "desktop.bold",
              lineHeight: "title.desktop.medium",
              fontSize: "title.desktop.medium",
              p: { pb: 8 },
            }}
            p={{ pb: 8 }}
          >
            {title}
          </Title>
        )}
        {description && <Text size="large" content={description} />}
      </Flex>
    </Flex>
  );
};
